/**
 * 認証リダイレクト関連のユーティリティ
 *
 * - アプリへの redirect_uri の許可リスト検証（トークン漏洩対策）
 * - Web 内でのトークン受け渡し（URL に載せず sessionStorage 経由で行う）
 * - Web 内リダイレクト先の内部パス限定（オープンリダイレクト対策）
 */

/** 既定のアプリ向けリダイレクトURI（デスクトップアプリのカスタムスキーム） */
export const DEFAULT_APP_REDIRECT_URI = 'kyumo-ai-agent-v2://auth/callback';

const TOKEN_HANDOFF_KEY = 'auth_token_handoff';

/**
 * アプリへのリダイレクト先として許可される URI の一覧
 */
function getAllowedRedirectUris(): string[] {
  const allowed = [DEFAULT_APP_REDIRECT_URI];
  const configured = import.meta.env.VITE_REDIRECT_URI;
  if (configured && !allowed.includes(configured)) {
    allowed.push(configured);
  }
  return allowed;
}

/**
 * redirect_uri を許可リストで検証する
 *
 * 許可されない URI（攻撃者が仕込んだ外部 URL 等）は無視して既定値を返す
 */
export function validateAppRedirectUri(uri: string | null | undefined): string {
  if (uri) {
    if (getAllowedRedirectUris().includes(uri)) {
      return uri;
    }
    console.warn('[Auth] 許可されていない redirect_uri を無視します:', uri);
  }
  return DEFAULT_APP_REDIRECT_URI;
}

/**
 * デスクトップアプリを前面に出すだけの URI（`<スキーム>://open`）。
 *
 * ブラウザで開いた画面（認証アプリの再登録など）から「アプリに戻る」ために使う。
 * スキームは許可リストで検証したリダイレクト URI から取り、外部の URL へは飛ばさない。
 * 何も受け渡さないため、トークンなどは付けない。
 */
export function getAppOpenUri(): string {
  const redirectUri = validateAppRedirectUri(import.meta.env.VITE_REDIRECT_URI);
  return `${new URL(redirectUri).protocol}//open`;
}

/**
 * Web 内で受け渡すトークン一式
 */
export interface TokenHandoff {
  idToken: string;
  accessToken: string;
  refreshToken?: string | undefined;
  state?: string | undefined;
}

/**
 * トークンを sessionStorage 経由で /auth/callback に受け渡す
 *
 * URL クエリに載せるとブラウザ履歴・アクセスログ・Referer に残留するため使わない
 */
export function storeTokenHandoff(handoff: TokenHandoff): void {
  sessionStorage.setItem(TOKEN_HANDOFF_KEY, JSON.stringify(handoff));
}

/**
 * 受け渡されたトークンを取り出す（取り出しと同時にストレージから削除する）
 */
export function consumeTokenHandoff(): TokenHandoff | null {
  const raw = sessionStorage.getItem(TOKEN_HANDOFF_KEY);
  if (!raw) {
    return null;
  }
  sessionStorage.removeItem(TOKEN_HANDOFF_KEY);
  try {
    const parsed = JSON.parse(raw) as Partial<TokenHandoff>;
    if (typeof parsed.idToken === 'string' && typeof parsed.accessToken === 'string') {
      return parsed as TokenHandoff;
    }
  } catch {
    // パース失敗時は null を返す
  }
  return null;
}

/**
 * Web 内リダイレクト先を内部パスに限定する（オープンリダイレクト対策）
 *
 * `//evil.com` のようなスキーム相対 URL や絶対 URL は拒否してフォールバック先を返す
 */
export function sanitizeInternalRedirect(
  path: string | null | undefined,
  fallback = '/download',
): string {
  if (!path) {
    return fallback;
  }

  // 二重エンコードによるバイパスを防ぐため、デコード後の値を検証する
  let decoded = path;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    return fallback;
  }

  if (decoded.startsWith('/') && !decoded.startsWith('//') && !decoded.startsWith('/\\')) {
    return decoded;
  }
  return fallback;
}
